import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { fullJitterRetryDelay } from '~/utils/jitter';

import { pauseRun, resumeRun } from '../services/run-detail.service';
import type { RunToggle } from '../types/run-detail.api.types';
import { newCorrelationId } from '../utils/correlation-id';
import { controlErrorMessage, isConflict, isNetworkFailure } from '../utils/run-control-errors';

/** Which way the toggle goes. */
export type RunToggleKind = 'pause' | 'resume';

/** Variables of one toggle click. */
interface ToggleVars {
  kind: RunToggleKind;
  body: RunToggle;
}

/** Resend budget for a dropped connection only — same body, same correlationId. */
const NETWORK_RETRIES = 2;

/**
 * Pause-at-next-stage / resume for the run header. One correlationId per click,
 * reused on a network retry; a 409 (run closed) toasts the server message and
 * refetches the run rather than retrying.
 */
export function useRunToggle(runRef: string) {
  const { t } = useTranslation('catalog-enrichment-run');
  const queryClient = useQueryClient();
  const correlationRef = useRef<string | null>(null);

  const { mutate, isPending, variables } = useMutation({
    mutationFn: ({ kind, body }: ToggleVars) =>
      kind === 'pause' ? pauseRun(runRef, body) : resumeRun(runRef, body),
    retry: (failureCount, err) => failureCount < NETWORK_RETRIES && isNetworkFailure(err),
    retryDelay: fullJitterRetryDelay,
    onSuccess: (ack, { kind }) => {
      if (ack.applied) toast.success(t(`header.toast.${kind}Applied`));
      else toast.info(t(`header.toast.${kind}NotApplied`));
    },
    onError: (err) => {
      const title = isConflict(err) ? t('header.toast.conflict') : t('header.toast.failed');
      toast.error(title, { description: controlErrorMessage(err, t('header.toast.failedBody')) });
    },
    onSettled: () => {
      correlationRef.current = null;
      // Pause/resume shows on the run AND its row in the runs grid.
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalogService.run(runRef) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalogService.runs.all() });
    },
  });

  const toggle = useCallback(
    (kind: RunToggleKind, reason?: string, options?: { onSuccess?: () => void }) => {
      if (isPending) return;
      correlationRef.current = newCorrelationId();
      const trimmed = reason?.trim();
      mutate(
        {
          kind,
          body: { correlationId: correlationRef.current, reason: trimmed?.length ? trimmed : null },
        },
        { onSuccess: () => options?.onSuccess?.() },
      );
    },
    [isPending, mutate],
  );

  return {
    toggle,
    isPending,
    pendingKind: isPending ? variables.kind : null,
  };
}
